// PRIMEIRO import de propósito: repõe métodos ES2022 antes de qualquer
// dependência ser avaliada (Chrome 90/91 quebrava o app inteiro sem eles).
import "@/lib/polyfills";
import { createRoot } from "react-dom/client";
import { setBaseUrl, setApiErrorReporter, type ReportableFetchError } from "@workspace/api-client-react";
import { reportError } from "@/components/ErrorBoundary";
import { vigiarGaleriaQueNaoAbre } from "@/lib/galeria-nao-abriu";
import { iniciarFilaDoAviso } from "@/lib/avisoConexao";
import App from "./App";
import "./index.css";

// Em produção com subdomínio separado (api.vendanozap.app), as chamadas
// relativas /api/* da SPA precisam virar absolutas. VITE_API_URL é injetada
// no build (fixa no vnz-deploy). Em dev (sem VITE_API_URL), o proxy
// do Vite cuida do redirecionamento e setBaseUrl(null) deixa o fetch nativo.
const apiBaseUrl = import.meta.env.VITE_API_URL?.trim();
setBaseUrl(apiBaseUrl && apiBaseUrl.length > 0 ? apiBaseUrl : null);

// Captura erros HTTP do customFetch em client_errors. Sem isso, 4xx/5xx
// morrem no .catch() do React Query e nunca chegam aos handlers
// window.error/unhandledrejection — fica invisivel no painel mesmo
// quando o lojista ve toast "HTTP 400". Filtramos respostas esperadas
// (401/403 fluxo de auth, 404 GET genérico) e a própria rota
// /api/client-errors (pra evitar loop infinito caso o endpoint quebre).
//
// Exceção (mai/2026): 404 GET em rotas públicas críticas do storefront
// /api/catalog/<slug>, /api/booking/<slug>, /api/pdv/<slug> — esses
// renderizam "Loja não encontrada" pro cliente final e o suporte precisa
// ver a URL exata pra diagnosticar typo do usuário (ex: pizzariax1 sem
// hífen) vs problema real (slug renomeado, store inativa).
const PUBLIC_CATALOG_PREFIXES = ["/api/catalog/", "/api/booking/", "/api/pdv/"];

setApiErrorReporter((err: ReportableFetchError) => {
  const url = err.url || "";
  if (url.includes("/api/client-errors")) return;
  // status 0 = NetworkError (sem resposta HTTP). Não passa pelos filtros de
  // faixa abaixo, que só fazem sentido para status real, e ganha rótulo
  // próprio: "rede" é o que se procura no painel quando o lojista diz que o
  // app "não abriu". ⚠️ Se a API estiver TOTALMENTE fora, este report também
  // não chega — o endpoint de telemetria é a mesma API. Ele cobre o que falha
  // do lado do cliente ou da borda: DNS, TLS, CORS, portal de operadora,
  // oscilação de rede móvel. Queda total quem enxerga é a sonda no KV.
  if (err.status === 0) {
    const enriched = new Error(`[${err.method} ${url}] ${err.message}`);
    enriched.name = "NetworkError";
    enriched.stack = err.stack;
    reportError(enriched, null, "rede");
    return;
  }
  if (err.status === 401 || err.status === 403) return;
  // 409 é REGRA DE NEGÓCIO por definição ("Já existe um ingrediente com esse
  // nome"): o servidor respondeu certo e a tela já mostra a mensagem ao
  // lojista. Registrar isso só afoga os erros de verdade no painel.
  // 400 fica DE FORA do filtro de propósito: foi justamente um 400
  // ("stack: Expected string, received null") que revelou a telemetria
  // quebrada em 27/07 — filtrar essa faixa nos cegaria de novo.
  if (err.status === 409) return;
  if (err.status === 404 && err.method === "GET") {
    const isPublicCatalog = PUBLIC_CATALOG_PREFIXES.some((p) => url.includes(p));
    if (!isPublicCatalog) return;
  }

  const enriched = new Error(
    `[${err.method} ${url} -> ${err.status}] ${err.message}`,
  );
  enriched.name = "ApiError";
  enriched.stack = err.stack;
  reportError(enriched, null, `http-${err.status}`);
});

vigiarGaleriaQueNaoAbre();
iniciarFilaDoAviso();

createRoot(document.getElementById("root")!).render(<App />);
